import type { HTMLAttributes, PropsWithChildren } from 'react';
import { Children } from 'react';

import { css } from '@compiled/react';

import { generateSafeIds } from '@/libraries/design/utils';

import { Adg4TabFC, mapChild } from './tab-helper';
import { useAdg4TabsContext } from './tabs';

/**
 * @deprecated - Please use @/libraries/design/patterns/tabs instead
 */
export type Adg4TabPanelProps = PropsWithChildren<{
	forceRender?: boolean;
	keepInDom?: boolean;
	panelIndex?: number;
	hideTabPanelRole?: boolean;
}> &
	HTMLAttributes<HTMLDivElement>;

const hidePanelStyle = css({
	display: 'none',
});

/**
 * @deprecated - Please use @/libraries/design/patterns/tabs instead
 */
const Adg4TabPanel: Adg4TabFC<Adg4TabPanelProps> = ({
	forceRender,
	keepInDom,
	children,
	panelIndex,
	id,
	hideTabPanelRole,
	...props
}: Adg4TabPanelProps) => {
	const { isCurrentTab } = useAdg4TabsContext();

	const isTabSelected = isCurrentTab(panelIndex);
	const activeClass = isTabSelected ? 'active-panel' : '';

	const originalChildrenCount = Children.count(children);
	const shouldHideRole =
		hideTabPanelRole !== undefined ? hideTabPanelRole : originalChildrenCount === 0;

	children = forceRender || isTabSelected || keepInDom ? children : null;

	const { panelId, tabId } = generateSafeIds(id, panelIndex);

	const { css: existingCss, ...propsWithoutCss } = props;

	const filteredProps = Object.fromEntries(
		Object.entries(propsWithoutCss).filter(([key]) => !key.startsWith('aria-') && key !== 'role'),
	) as typeof propsWithoutCss;

	if (!shouldHideRole) {
		return (
			<div
				{...filteredProps}
				css={[!isTabSelected && !forceRender && hidePanelStyle]}
				id={panelId}
				aria-labelledby={tabId}
				aria-hidden={!isTabSelected}
				role="tabpanel"
				className={['tab-panel', activeClass].join(' ')}
			>
				{Children.map(children, (child) => {
					return mapChild(child, { isPanelActive: isTabSelected });
				})}
			</div>
		);
	}

	const cssProp = [!isTabSelected && !forceRender && hidePanelStyle];

	return (
		<div
			css={cssProp}
			id={panelId}
			className={['tab-panel', activeClass].join(' ')}
			aria-labelledby={undefined}
			aria-hidden={undefined}
			role={undefined}
		>
			{Children.map(children, (child) => {
				return mapChild(child, { isPanelActive: isTabSelected });
			})}
		</div>
	);
};
Adg4TabPanel.tabsRole = 'Adg4TabPanel';

export { Adg4TabPanel };
